import React, { useState, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import "../../styles/CategoryProductList.css";
import { ERROR, SUCCESS } from "../../include/constant";
import { useNavigate } from "react-router-dom";
import { getAdmin, req, tooLong } from "../../include/function";
import Select from "react-select";
import CustomNumberInput from "../custom/CustomNumberInput";

const AddCoupon = ({ type }) => {
  const adminUser = getAdmin();
  const toast = useNotification();
  const navigate = useNavigate();
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  // URL parsing
  const urlParams = new URLSearchParams(window.location.search);
  const couponIdFromUrl = urlParams.get("id");
  const isEditMode = !!couponIdFromUrl;

  // State
  const [saving, setSaving] = useState(false);

  // Form fields
  const [couponCode, setCouponCode] = useState("");
  const [couponType, setCouponType] = useState("");
  const [basedOn, setBasedOn] = useState("quantity");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [value, setValue] = useState("");
  const [b2bB2c, setB2bB2c] = useState([]);
  const [customerType, setCustomerType] = useState([]);
  const [minimumRequirement, setMinimumRequirement] = useState("");
  const [usageLimit, setUsageLimit] = useState("");

  // Error states
  const [couponCodeError, setCouponCodeError] = useState("");
  const [couponTypeError, setCouponTypeError] = useState("");
  const [basedOnError, setBasedOnError] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [valueError, setValueError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [b2bB2cError, setB2bB2cError] = useState("");
  const [minimumRequirementError, setMinimumRequirementError] = useState("");
  const [usageLimitError, setUsageLimitError] = useState("");

  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  // BUTTON STYLING HELPERS
  const getButtonStyle = (isPrimary = false) => ({
    border: "none",
    padding: "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    ...(isPrimary 
      ? { 
          backgroundColor: saving ? "#1565c0" : "#1976d2", 
          color: "#fff" 
        } 
      : { 
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff", 
          color: saving ? "#9e9e9e" : "#1976d2" 
        }
    ),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i 
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`} 
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  // Load categories
  useEffect(() => {
    api
      .get("/category/?flag=exceptHome", adminReqConfig())
      .then((res) =>
        setCategoryOptions(
          (res.data.data || [])
            .filter((cat) => cat.parentId === 0)
            .map((c) => ({ value: c.id, label: c.name })),
        ),
      )
      .catch(() => setCategoryOptions([]));
  }, []);

  // Load all users
  useEffect(() => {
    api
      .get("/customer/", adminReqConfig())
      .then((res) => {
        setAllUsers(res.data.data || []);
        setUserOptions(
          res.data.data.map((c) => ({ value: c.id, label: c.name })),
        );
      })
      .catch(() => {
        setAllUsers([]);
        setUserOptions([]);
      });
  }, []);

  const fetchCouponDetails = (id) => {
    api
      .get(`/voucher/?id=${id}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const coupon = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          if (!coupon) return;

          // Basic fields
          setCouponCode(coupon.code || "");
          setCouponType(coupon.type || "");
          setBasedOn(coupon.basedOn || "quantity");
          setStartDate(coupon.startDate || "");
          setEndDate(coupon.endDate || "");
          setValue(String(coupon.value) || "");
          setMinimumRequirement(String(coupon.minRequirement) || "");
          setUsageLimit(String(coupon.maxCount) || "");

          // Helper to convert ["id1", "id2"] + ["Name1", "Name2"] → [{value, label}]
          const mapToSelectOptions = (ids, names) => {
            if (!Array.isArray(ids)) return [];
            return ids.map((id, index) => ({
              value: id,
              label: names?.[index] || `ID ${id}`,
            }));
          };

          // Category
          setSelectedCategory(
            mapToSelectOptions(coupon.category, coupon.categoryName),
          );

          // Brand
          setSelectedBrand(mapToSelectOptions(coupon.brand, coupon.brandName));

          // Product
          setSelectedProduct(
            mapToSelectOptions(coupon.product, coupon.productName),
          );

          // Customer Group
          setB2bB2c(
            (coupon.customerGroup || []).map((value) => ({
              value,
              label: value === "b2c" ? "B2C Customer" : "B2B Customer",
            })),
          );

          // Customer Type
          setCustomerType(
            (coupon.customerType || []).map((value) => ({
              value,
              label: value
                .replace(/-/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()), // "sub-dealer" → "Sub Dealer"
            })),
          );

          // User
          setSelectedUser(
            mapToSelectOptions(coupon.customer, coupon.customerName),
          );
        }
      })
      .catch(() => {
        toast({ type: "error", message: "Failed to load coupon details." });
      });
  };

  useEffect(() => {
    if (isEditMode && couponIdFromUrl) {
      fetchCouponDetails(couponIdFromUrl);
    }
  }, [isEditMode, couponIdFromUrl]);

  // Load brands when category changes
  useEffect(() => {
    if (!selectedCategory || selectedCategory.length === 0) {
      setBrandOptions([]);
      setSelectedBrand([]);
      setProductOptions([]);
      setSelectedProduct([]);
      return;
    }

    const categoryIds = selectedCategory.map((cat) => cat.value);
    api
      .get(`/brand/?categoryId=${categoryIds.join(",")}`, adminReqConfig())
      .then((res) => {
        const brands =
          res.data.data?.map((b) => ({ value: b.id, label: b.name })) || [];
        setBrandOptions(brands);
        // Don't reset selectedBrand if we're in edit mode and it was already set
        if (!isEditMode) {
          setSelectedBrand([]);
          setProductOptions([]);
          setSelectedProduct([]);
        }
      })
      .catch(() => {
        setBrandOptions([]);
        if (!isEditMode) {
          setSelectedBrand([]);
          setProductOptions([]);
          setSelectedProduct([]);
        }
      });
  }, [selectedCategory, isEditMode]);

  // Load products when brand changes
  useEffect(() => {
    if (!selectedBrand || selectedBrand.length === 0) {
      setProductOptions([]);
      if (!isEditMode) setSelectedProduct([]);
      return;
    }

    const brandIds = selectedBrand.map((brand) => brand.value);
    const categoryIds = selectedCategory.map((cat) => cat.value);

    api
      .get(
        `/product/?categoryId=${categoryIds.join(",")}&brandId=${brandIds.join(
          ",",
        )}`,
        adminReqConfig(),
      )
      .then((res) => {
        const products =
          res.data.data?.map((p) => ({ value: p.productId, label: p.name })) ||
          [];
        setProductOptions(products);
        if (!isEditMode) setSelectedProduct([]);
      })
      .catch(() => {
        setProductOptions([]);
        if (!isEditMode) setSelectedProduct([]);
      });
  }, [selectedCategory, selectedBrand, isEditMode]);

  const b2bB2cOptions = [
    { value: "b2c", label: "B2C Customer" },
    { value: "b2b", label: "B2B Customer" },
  ];

  const getCustomerTypeOptions = () => {
    const options = [];
    const selectedGroups = b2bB2c.map((opt) => opt.value);

    if (selectedGroups.includes("b2b")) {
      options.push(
        { value: "dealer", label: "Dealer" },
        { value: "sub-dealer", label: "Sub-Dealer" },
        { value: "contractor", label: "Contractor" },
        { value: "builder", label: "Builder" },
        { value: "engineer", label: "Engineer" },
        { value: "architect", label: "Architect" },
      );
    }
    if (selectedGroups.includes("b2c")) {
      options.push({ value: "individual", label: "Individual" });
    }
    return options;
  };

  useEffect(() => {
    const selectedGroups = b2bB2c.map((g) => g.value);
    const selectedTypes = customerType.map((t) => t.value);

    let filtered = allUsers.filter((user) => {
      const type = user.customerType?.toLowerCase() || "";
      const group = type === "individual" ? "b2c" : "b2b";

      if (!selectedGroups.includes(group)) return false;
      if (selectedTypes.length > 0) {
        return selectedTypes.includes(type);
      }
      return true;
    });

    setUserOptions(filtered.map((u) => ({ value: u.id, label: u.name })));

    // 🔑 Only auto-update selectedUser in CREATE mode
    if (!isEditMode) {
      setSelectedUser((prev) =>
        prev.filter((u) => filtered.some((f) => f.id === u.value)),
      );
    }
  }, [b2bB2c, customerType, allUsers, isEditMode]);

  // Clear functions - ✅ FIXED: use [] instead of null
  const clearCouponInfo = () => {
    if (saving) return;
    setCouponCode("");
    setCouponType("");
    setBasedOn("quantity");
    setCouponCodeError("");
    setCouponTypeError("");
  };

  const clearValidFrom = () => {
    if (saving) return;
    setStartDate("");
    setEndDate("");
    setValue("");
    setStartDateError("");
    setEndDateError("");
    setValueError("");
  };

  const clearApplicableItems = () => {
    if (saving) return;
    setSelectedCategory([]); // ✅ array, not null
    setSelectedBrand([]); // ✅
    setSelectedProduct([]); // ✅
    setCategoryError("");
  };

  const clearApplicableCustomers = () => {
    if (saving) return;
    setB2bB2c([]);
    setCustomerType([]);
    setSelectedUser([]);
    setB2bB2cError("");
  };

  const clearMinimumRequirements = () => {
    if (saving) return;
    setMinimumRequirement("");
    setUsageLimit("");
    setMinimumRequirementError("");
    setUsageLimitError("");
  };

  const clearAll = () => {
    if (saving) return;
    clearCouponInfo();
    clearValidFrom();
    clearApplicableItems();
    clearApplicableCustomers();
    clearMinimumRequirements();
    setBasedOn("quantity");
  };

  const minimumRequirementPlaceholder =
    basedOn === "quantity" ? "Enter the quantity" : "Enter the amount";

  const handleSave = () => {
    let error = false;

    setCouponCodeError("");
    setCouponTypeError("");
    setStartDateError("");
    setEndDateError("");
    setValueError("");
    setCategoryError("");
    setB2bB2cError("");
    setMinimumRequirementError("");
    setUsageLimitError("");

    if (!couponCode.trim()) {
      setCouponCodeError(req("Coupon Code"));
      error = true;
    } else if (couponCode.length > 100) {
      setCouponCodeError(tooLong("Coupon Code"));
      error = true;
    }

    if (!couponType) {
      setCouponTypeError("Coupon Type is required");
      error = true;
    }

    if (!selectedCategory || selectedCategory.length === 0) {
      setCategoryError("Category is required");
      error = true;
    }

    if (!minimumRequirement) {
      setMinimumRequirementError("Minimum requirement is required");
      error = true;
    }

    if (!usageLimit) {
      setUsageLimitError("Usage Limit is required");
      error = true;
    }

    if (!startDate) {
      setStartDateError("Start Date is required");
      error = true;
    }

    if (!endDate) {
      setEndDateError("End Date is required");
      error = true;
    }

    if (!value.trim()) {
      setValueError("Value is required");
      error = true;
    }

    if (b2bB2c.length === 0) {
      setB2bB2cError("B2B/B2C is required");
      error = true;
    }

    if (error) return;

    const data = {
      code: couponCode.trim(),
      type: couponType.trim(),
      basedOn: basedOn.trim(),
      startDate: startDate.trim(),
      endDate: endDate.trim(),
      value: value.trim(),
      category: selectedCategory.map((c) => c.value),
      brand: selectedBrand.map((b) => b.value),
      product: selectedProduct.map((p) => p.value),
      customerGroup: b2bB2c.map((g) => g.value),
      customerType: customerType.map((t) => t.value),
      customer: selectedUser.map((u) => u.value),
      minRequirement: minimumRequirement.trim(),
      maxCount: usageLimit.trim(),
      createdBy: "admin",
    };

    setSaving(true);

    const savePromise = isEditMode
      ? api.put(`/voucher/?id=${couponIdFromUrl}`, data, adminReqConfig())
      : api.post("/voucher/", data, adminReqConfig());

    savePromise
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: isEditMode
              ? "Coupon updated successfully!"
              : "Coupon added successfully!",
          });
          navigate("/admin/coupon/couponList");
          if (!isEditMode) clearAll();
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || "Failed to save coupon.",
        });
      })
      .finally(() => setSaving(false));
  };

  const selectStyles = {
    container: (provided) => ({
      ...provided,
      width: "300px",
      maxWidth: "100%",
      margin: 0,
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "54px",
      height: "54px",
      padding: "0 8px",
      border: "1px solid #c7d7f5",
      borderRadius: "5px",
      boxShadow: "none",
      backgroundColor: state.isDisabled ? "#f8f9fa" : "transparent",
      overflowX: "auto",
      overflowY: "hidden",
      "& > div:first-child": {
        display: "flex",
        flexWrap: "nowrap",
        minWidth: "fit-content",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      flexWrap: "nowrap",
      display: "flex",
      alignItems: "center",
      minHeight: "40px",
    }),
    multiValue: (provided) => ({
      ...provided,
      margin: "4px 2px",
      backgroundColor: "#e3f2fd",
      border: "1px solid #123456",
      borderRadius: "4px",
      padding: "2px 6px",
      whiteSpace: "nowrap",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      minWidth: "80px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999",
      whiteSpace: "nowrap",
      position: "absolute",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "40px",
      alignSelf: "center",
    }),
  };

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title={isEditMode ? "EDIT COUPON" : "ADD COUPON"}
      />
      <div
        className="content-wrapper p-0"
        style={{
          marginLeft: isSidebarMinimized ? "80px" : "250px",
          flex: "1",
        }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
          }}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card border-0">
                  <div className="d-flex justify-content-between align-items-center p-16">
                    <h6 className="mb-0 fs-5">Coupon Information</h6>
                    <button
                      onClick={clearCouponInfo}
                      disabled={saving}
                      style={getButtonStyle(false)}
                    >
                      {saving ? (
                        <>
                          {renderLoadingSpinner("sm")}
                          Clearing...
                        </>
                      ) : (
                        "Clear"
                      )}
                    </button>
                  </div>
                  <div className="card-body">
                    {/* Coupon Code & Type */}
                    <div className="row">
                      <div className="col-md-6 mb-16">
                        <h6 className="text-md mb-16 fw-bold">Coupon Code</h6>
                        <input
                          type="text"
                          className="form-control coupon-add-input"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => !saving && setCouponCode(e.target.value)}
                          disabled={saving}
                        />
                        {couponCodeError && (
                          <div className="text-danger mt-1">
                            {couponCodeError}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-16">
                        <h6 className="text-md mb-16 fw-bold">Coupon Type</h6>
                        <select
                          className="form-select coupon-add-input"
                          value={couponType}
                          onChange={(e) => !saving && setCouponType(e.target.value)}
                          disabled={saving}
                        >
                          <option value="" disabled>
                            Select Type
                          </option>
                          <option value="percentage">Percentage</option>
                          <option value="price">Price</option>
                        </select>
                        {couponTypeError && (
                          <div className="text-danger mt-1">
                            {couponTypeError}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Per Quantity / Per Order */}
                    <div className="row mb-16">
                      <div className="col-md-12">
                        <h6 className="text-md mb-16 fw-bold">Select Coupon</h6>
                        <div className="d-flex gap-4">
                          <label className="d-flex align-items-center">
                            <input
                              type="radio"
                              className="me-2"
                              checked={basedOn === "quantity"}
                              onChange={() => !saving && setBasedOn("quantity")}
                              disabled={saving}
                            />
                            Per Quantity
                          </label>
                          <label className="d-flex align-items-center">
                            <input
                              type="radio"
                              className="me-2"
                              checked={basedOn === "order"}
                              onChange={() => !saving && setBasedOn("order")}
                              disabled={saving}
                            />
                            Per Order
                          </label>
                        </div>
                        {basedOnError && (
                          <div className="text-danger mt-1">{basedOnError}</div>
                        )}
                      </div>
                    </div>

                    {/* Valid From */}
                    <div className="mt-20">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fs-6">Valid From</h6>
                        <button
                          onClick={clearValidFrom}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      </div>
                      <div className="px-14">
                        <div className="row mt-16 coupon-Add-row">
                          <div className="col-md-12">
                            <div className="d-flex gap-20">
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-16 fw-bold">
                                  Start Date
                                </h6>
                                <input
                                  type="date"
                                  className="form-control coupon-add-input"
                                  value={startDate}
                                  onChange={(e) => !saving && setStartDate(e.target.value)}
                                  disabled={saving}
                                />
                                {startDateError && (
                                  <div className="text-danger mt-1">
                                    {startDateError}
                                  </div>
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-16 fw-bold">
                                  End Date
                                </h6>
                                <input
                                  type="date"
                                  className="form-control coupon-add-input"
                                  value={endDate}
                                  onChange={(e) => !saving && setEndDate(e.target.value)}
                                  disabled={saving}
                                />
                                {endDateError && (
                                  <div className="text-danger mt-1">
                                    {endDateError}
                                  </div>
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-16 fw-bold">Value</h6>
                                <CustomNumberInput
                                  value={value}
                                  onChange={(val) => !saving && setValue(val)}
                                  className="form-control coupon-add-input"
                                  placeholder="Enter amount"
                                  disabled={saving}
                                />
                                {valueError && (
                                  <div className="text-danger mt-1">
                                    {valueError}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Applicable Items */}
                    <div className="mt-20">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fs-6">Applicable Items</h6>
                        <button
                          onClick={clearApplicableItems}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      </div>
                      <div className="px-14">
                        <div className="row mt-16 coupon-Add-row">
                          <div className="col-md-12">
                            <div className="d-flex gap-20">
                              {/* Category */}
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-8 fw-bold">
                                  Category
                                </h6>
                                <Select
                                  isMulti
                                  value={selectedCategory}
                                  onChange={(val) => !saving && setSelectedCategory(val)}
                                  options={categoryOptions}
                                  placeholder="Select category"
                                  isClearable
                                  isSearchable
                                  isDisabled={saving}
                                  className="coupon-add-select"
                                  classNamePrefix="select"
                                  styles={selectStyles}
                                />
                                {categoryError && (
                                  <div className="text-danger mt-1">
                                    {categoryError}
                                  </div>
                                )}
                              </div>

                              {/* Brand */}
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-8 fw-bold">Brand</h6>
                                <Select
                                  isMulti
                                  value={selectedBrand}
                                  onChange={(val) => !saving && setSelectedBrand(val)}
                                  options={brandOptions}
                                  placeholder={
                                    selectedCategory.length > 0
                                      ? "Select brand"
                                      : "Select category first"
                                  }
                                  isClearable
                                  isSearchable
                                  isDisabled={saving || selectedCategory.length === 0}
                                  className="coupon-add-select"
                                  classNamePrefix="select"
                                  styles={selectStyles}
                                />
                              </div>

                              {/* Product */}
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-8 fw-bold">
                                  Product
                                </h6>
                                <Select
                                  isMulti
                                  value={selectedProduct}
                                  onChange={(val) => !saving && setSelectedProduct(val)}
                                  options={productOptions}
                                  placeholder={
                                    selectedBrand.length > 0
                                      ? "Select product"
                                      : "Select brand first"
                                  }
                                  isClearable
                                  isSearchable
                                  isDisabled={saving || selectedBrand.length === 0}
                                  className="coupon-add-select"
                                  classNamePrefix="select"
                                  styles={selectStyles}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Applicable Customers */}
                    <div className="mt-20">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fs-6">Applicable Customers</h6>
                        <button
                          onClick={clearApplicableCustomers}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      </div>
                      <div className="px-14">
                        <div className="row mt-16 coupon-Add-row">
                          <div className="col-md-12">
                            <div className="d-flex gap-20">
                              {/* B2B/B2C */}
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-8 fw-bold">
                                  B2B / B2C
                                </h6>
                                <Select
                                  isMulti
                                  value={b2bB2c}
                                  onChange={(val) => !saving && setB2bB2c(val)}
                                  options={b2bB2cOptions}
                                  placeholder="Select customer groups"
                                  isClearable
                                  isSearchable
                                  isDisabled={saving}
                                  className="coupon-add-select"
                                  classNamePrefix="select"
                                  styles={selectStyles}
                                />
                                {b2bB2cError && (
                                  <div className="text-danger mt-1">
                                    {b2bB2cError}
                                  </div>
                                )}
                              </div>

                              {/* Customer Type */}
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-8 fw-bold">
                                  Customer Type
                                </h6>
                                <Select
                                  isMulti
                                  value={customerType}
                                  onChange={(val) => !saving && setCustomerType(val)}
                                  options={getCustomerTypeOptions()}
                                  placeholder="Select customer types"
                                  isClearable
                                  isSearchable
                                  isDisabled={saving}
                                  className="coupon-add-select"
                                  classNamePrefix="select"
                                  styles={selectStyles}
                                />
                              </div>

                              {/* User */}
                              <div className="flex-grow-1">
                                <h6 className="text-md mb-8 fw-bold">User</h6>
                                <Select
                                  isMulti
                                  value={selectedUser}
                                  onChange={(val) => !saving && setSelectedUser(val)}
                                  options={userOptions}
                                  placeholder="Select users"
                                  isClearable
                                  isSearchable
                                  isDisabled={saving}
                                  classNamePrefix="select"
                                  styles={selectStyles}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Minimum Requirements & Usage Limit */}
                    <div className="mt-20">
                      <div className="row">
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">
                            Minimum Requirements
                          </h6>
                          <input
                            type="text"
                            className="form-control coupon-add-input"
                            placeholder={minimumRequirementPlaceholder}
                            value={minimumRequirement}
                            onChange={(e) => !saving && setMinimumRequirement(e.target.value)}
                            disabled={saving}
                          />
                          {minimumRequirementError && (
                            <div className="text-danger mt-1">
                              {minimumRequirementError}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-16">
                          <h6 className="text-md mb-16 fw-bold">Usage Limit</h6>
                          <CustomNumberInput
                            className="form-control coupon-add-input"
                            value={usageLimit}
                            onChange={(val) => !saving && setUsageLimit(val)}
                            placeholder="Enter the limit"
                            disabled={saving}
                          />
                          {usageLimitError && (
                            <div className="text-danger mt-1">
                              {usageLimitError}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Save & Clear Buttons */}
                    <div className="d-flex justify-content-end gap-8 mt-24">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        style={getButtonStyle(true)}
                      >
                        {saving ? (
                          <>
                            {renderLoadingSpinner("sm")}
                            {isEditMode ? "Updating..." : "Adding..."}
                          </>
                        ) : isEditMode ? (
                          "Update"
                        ) : (
                          "Add"
                        )}
                      </button>
                      <button
                        onClick={clearAll}
                        disabled={saving}
                        style={getButtonStyle(false)}
                      >
                        {saving ? (
                          <>
                            {renderLoadingSpinner("sm")}
                            Clearing...
                          </>
                        ) : (
                          "Clear All"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <AdminFooter isSidebarMinimized={isSidebarMinimized} />
    </>
  );
};

export default AddCoupon;